import OnboardingPageDrawer from "@app/(views)/my-projects/[id]/on-boarding/OnBoardingPageDrawer";
import useProjectId from "@libs/customHooks/useProjectId";
import { useProjectPermission } from "@libs/customHooks/useProjectPermission";
import useQueryData from "@libs/customHooks/useQueryData";
import { VeSearch } from "@packages/ui/components";
import { limiter } from "@packages/ui/hoc";
import { faCheck, faChevronDown, faChevronUp, faPlus } from "@packages/ui/libs";
import {
  CircularProgress,
  FontAwesomeIcon,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  VeBox,
  VeTypography,
} from "@packages/ui/mui";
import { useData } from "@providers/context";
import { COLLABORATORS, ORG_ICON, TICK } from "@public/index";
import { isArray } from "lodash";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";

const PROJECT_ID_KEY = "VERICAP_PROJECT_ID";

const ProjectSelector = () => {
  const [anchorElUserTab, setAnchorElUserTab] =
    React.useState<null | HTMLElement>(null);
  const [projects, setProjects] = React.useState<any>([]);
  const [selectedProject, setSelectedProject] = React.useState<any>(null);
  const [projectSearch, setProjectSearch] = React.useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [filteredProjects, setFilteredProjects] = React.useState<any>([]);
  const [addProject, setAddProject] = useState(false);
  const [accessLevel, setAccessLevel] = useState<
    "Organisation" | "Collaborator"
  >("Organisation");
  const [organisationProjectCount, setOrganisationProjectCount] =
    useState<number>(0);
  const [collaboratorProjectCount, setCollaboratorProjectCount] =
    useState<number>(0);
  const { userData } = useData();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const pathName = usePathname();
  const { projectPermissionCheck } = useProjectPermission();
  const canViewProject = projectPermissionCheck(
    "project-details.basic-info.view"
  );
  const { data, isFetching, error, refetch } = useQueryData({
    queryKey: [
      "drafts",
      page * rowsPerPage,
      rowsPerPage,
      searchQuery,
      accessLevel,
      canViewProject
    ],
    endPoint: `/project/onboarding?${searchQuery.length ? `&projectName=${searchQuery}` : ""
      }&accessLevel=${accessLevel}&offset=${page * rowsPerPage
      }&limit=${rowsPerPage}`,
    // enabled: canViewProject,
  });
  const projectId = useProjectId();
  const { data: basicDetails } = useQueryData({
    queryKey: ["get-project-basic-details-v2", projectId, canViewProject],
    endPoint: `/project/v2/onboarding/${projectId}`,
    enabled: !!projectId && canViewProject,
  });

  const { setProjectPermission, setProjectPermissionLoading } = useData();
  const { data: projectPermissionData, isLoading } = useQueryData({
    queryKey: ["project-permission", projectId],
    endPoint: `/roles/project-permission?projectId=${projectId}`,
    enabled: !!projectId,
  });

  useEffect(() => {
    if (projectPermissionData) {
      setProjectPermission((prev: any) => ({
        ...prev,
        [projectId]: projectPermissionData?.permissions,
      }));
    }
    setProjectPermissionLoading(isLoading);
  }, [projectPermissionData, isLoading]);

  // Check initial counts for both access levels
  const { data: orgCountData } = useQueryData({
    queryKey: ["org-project-count"],
    endPoint: `/project/onboarding?accessLevel=Organisation&offset=0&limit=1`,
    enabled: organisationProjectCount === 0 && collaboratorProjectCount === 0,
  });

  const { data: collabCountData } = useQueryData({
    queryKey: ["collab-project-count"],
    endPoint: `/project/onboarding?accessLevel=Collaborator&offset=0&limit=1`,
    enabled: organisationProjectCount === 0 && collaboratorProjectCount === 0,
  });

  // Set initial counts
  useEffect(() => {
    if (orgCountData?.count !== undefined && organisationProjectCount === 0) {
      setOrganisationProjectCount(orgCountData.count);
    }
    if (
      collabCountData?.count !== undefined &&
      collaboratorProjectCount === 0
    ) {
      setCollaboratorProjectCount(collabCountData.count);
    }
  }, [orgCountData, collabCountData]);

  // Auto-switch to tab with projects if no project is selected
  useEffect(() => {
    if (
      !selectedProject &&
      organisationProjectCount === 0 &&
      collaboratorProjectCount > 0
    ) {
      setAccessLevel("Collaborator");
    } else if (
      !selectedProject &&
      collaboratorProjectCount === 0 &&
      organisationProjectCount > 0
    ) {
      setAccessLevel("Organisation");
    }
  }, [selectedProject, organisationProjectCount, collaboratorProjectCount]);

  useEffect(() => {
    // Only select a project if there's a projectId in the URL
    if (projectId && basicDetails?.project) {
      setSelectedProject(basicDetails.project);
      // Automatically set accessLevel based on selected project's accessLevel
      if (basicDetails.project.accessLevel) {
        setAccessLevel(basicDetails.project.accessLevel);
      }
    } else if (!projectId) {
      // Clear selected project if there's no projectId in URL
      setSelectedProject(null);
    }
  }, [projectId, basicDetails]);

  useEffect(() => {
    if (data?.projects) {
      if (searchQuery?.length === 0) {
        const updatedProjects: any = [...data.projects];
        // Only add selectedProject to the list if it matches the current accessLevel
        if (
          selectedProject &&
          selectedProject.accessLevel === accessLevel &&
          !updatedProjects.some(
            (project: any) => project.id === selectedProject.id
          )
        ) {
          updatedProjects.unshift(selectedProject);
        }
        setProjects(updatedProjects);
        setFilteredProjects(updatedProjects);
      } else {
        setProjects(data.projects);
        setFilteredProjects(data.projects);
      }

      // Update counts based on current access level
      if (accessLevel === "Organisation") {
        setOrganisationProjectCount(data.count || 0);
      } else {
        setCollaboratorProjectCount(data.count || 0);
      }

      // Don't auto-select any project - let user explicitly choose
      // The selectedProject will only be set when:
      // 1. User clicks on a project from the dropdown
      // 2. There's a projectId in the URL (handled by basicDetails useEffect)

      // 👉 Set first project's ID in sessionStorage if no projectId exists
      if (
        !projectId &&
        data?.projects?.length > 0 &&
        typeof window !== "undefined"
      ) {
        const firstProjectId = data.projects[0].id;
        sessionStorage.setItem(PROJECT_ID_KEY, firstProjectId);
        // Dispatch custom event to notify other components of sessionStorage change
        window.dispatchEvent(new Event("sessionStorageChange"));
      }
    } else {
      if (isArray(data?.projects) && data?.projects?.length === 0) {
        setProjects([]);
        setFilteredProjects([]);
        // Update counts for empty results
        if (accessLevel === "Organisation") {
          setOrganisationProjectCount(0);
        } else {
          setCollaboratorProjectCount(0);
        }
      }
    }
  }, [data, selectedProject, projectId, accessLevel]);

  useEffect(() => {
    if (searchQuery?.length) {
      // TODO
      setRowsPerPage(8);
      setPage(0);
    }
  }, [searchQuery]);

  useEffect(() => {
    const interval = setTimeout(() => {
      setSearchQuery(projectSearch);
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [projectSearch]);

  const handleOpenUserMenuTab = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUserTab(event.currentTarget);
  };

  const handleCloseUserMenuTab = () => {
    setAnchorElUserTab(null);
    setProjectSearch("");
    setFilteredProjects(projects);
  };
  const router = useRouter();

  const handleProjectChange = (project: any) => {
    setSelectedProject(project);

    // Update sessionStorage with new projectId
    if (typeof window !== "undefined") {
      sessionStorage.setItem(PROJECT_ID_KEY, project.id);
      window.dispatchEvent(new Event("sessionStorageChange"));
    }

    // Get current path segments
    const pathSegments = pathName.split("/").filter(Boolean);

    // Check if we're currently on a project route (contains manage-project or project)
    const projectRouteIndex = pathSegments.findIndex(
      (segment) => segment === "manage-project" || segment === "project"
    );
    const isOnProjectRoute = projectRouteIndex !== -1;

    if (isOnProjectRoute) {
      const baseRoute = pathSegments[projectRouteIndex];

      if (pathSegments.length > projectRouteIndex + 2) {
        const subRouteParts = pathSegments.slice(projectRouteIndex + 2);

        // If on a facility detail page e.g. /facilities/[facilityId]/...
        // redirect to just /facilities to avoid carrying over old facilityId
        const facilitiesIndex = subRouteParts.indexOf("facilities");
        const isFacilityDetailPage =
          facilitiesIndex !== -1 &&
          subRouteParts[facilitiesIndex + 1] !== undefined;

        if (isFacilityDetailPage) {
          router.push(`/${baseRoute}/${project.id}/facilities`);
        } else {
          const routeAfterProjectId = subRouteParts.join("/");
          router.push(`/${baseRoute}/${project.id}/${routeAfterProjectId}`);
        }
      } else {
        router.push(`/manage-project/${project.id}/overview`);
      }
    } else {
      router.push(`/manage-project/${project.id}/overview`);
    }

    handleCloseUserMenuTab();
  };

  const handleAddProject = () => {
    handleCloseUserMenuTab();
    const mailtoLink = `mailto:support@vericap.io?subject=Request to Add New Project&body=Dear [Admin/Team],%0D%0A%0D%0AI would like to request the addition of a new project to my account. Below are the details:%0D%0A%0D%0AProject Name: [Enter Project Name]%0D%0AProject Type: [Enter Project Type]%0D%0A%0D%0ABest regards,%0D%0A[PD Name]`;
    window.location.href = mailtoLink;
  };

  const handleProjectSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProjectSearch(value);
  };

  const clearSearchFunction = () => {
    setProjectSearch("");
    setFilteredProjects(projects);
  };

  const showMoreProjects = () => {
    // setPage((prev) => prev + 1);
    setRowsPerPage((prev) => prev + 8);
    refetch();
  };

  const isMenuDisabled = pathName?.includes("/project");

  return (
    <VeBox>
      <VeBox
        onClick={(e: any) => !isMenuDisabled && handleOpenUserMenuTab(e)}
        display="flex"
        alignItems="center"
        marginRight={"19px"}
      >
        <IconButton
          sx={{
            padding: "4px 8px",
            borderRadius: "4px",
            background: Boolean(anchorElUserTab) ? "#F3F3F3" : "",
            ":hover": {
              backgroundColor: "transparent",
            },
          }}
          disabled={isMenuDisabled}
        >
          <VeBox
            display="flex"
            gap={"8px"}
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={{ cursor: "pointer", minWidth: "100px", maxWidth: "250px" }}
          >
            <VeTypography variant="label2Main">
              {selectedProject
                ? limiter({
                  strings: selectedProject?.isOrg
                    ? userData?.user?.organisationName || ""
                    : selectedProject?.projectName,
                  limit: isMobile ? 18 : 24,
                })
                : "Select Project"}
            </VeTypography>
            {(projects?.length > 1 || !selectedProject) && (
              <FontAwesomeIcon
                icon={faChevronDown}
                size="2xs"
                style={{ color: "#5f6368", fontSize: "12px" }}
              />
            )}
          </VeBox>
        </IconButton>
      </VeBox>
      <Menu
        sx={{ mt: "28px" }}
        disableScrollLock
        anchorEl={anchorElUserTab}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={Boolean(anchorElUserTab)}
        onClose={handleCloseUserMenuTab}
        MenuListProps={{
          disablePadding: true,
        }}
        slotProps={{
          paper: {
            sx: {
              padding: "8px",
              // minHeight: "220px",
              maxHeight: "530px",
              overflowY: "auto",
              width: isMobile ? "250" : "420px",
              boxShadow:
                "0px 2px 16px 0px rgba(0, 0, 0, 0.08), 0px 2px 8px -2px rgba(0, 0, 0, 0.12)",
            },
          },
        }}
      >
        <VeBox
          display={"flex"}
          marginBottom={"8px"}
          gap={"16px"}
          border="1px solid #E8E8E8"
          padding="4px"
          borderRadius={"8px"}
          marginX={"4px"}
        >
          <VeBox
            padding={"6px 12px"}
            width={"100%"}
            sx={{
              background: accessLevel === "Organisation" ? "#EFE9FB" : "white",
              cursor: organisationProjectCount > 0 ? "pointer" : "not-allowed",
              opacity: organisationProjectCount > 0 ? 1 : 0.5,
            }}
            display={"flex"}
            justifyContent={"center"}
            onClick={() => {
              if (organisationProjectCount > 0) {
                setAccessLevel("Organisation");
                setPage(0);
                setProjectSearch("");
              }
            }}
            borderRadius={"4px"}
            gap={"8px"}
            alignItems={"center"}
          >
            <Image src={ORG_ICON} alt="ORG_ICON" height={16} width={16} />
            <VeTypography variant={"label3StrongMain"}>
              Organisation Projects
            </VeTypography>
          </VeBox>
          <VeBox
            padding={"6px 12px"}
            width={"100%"}
            display={"flex"}
            justifyContent={"center"}
            borderRadius={"4px"}
            sx={{
              background: accessLevel === "Collaborator" ? "#EFE9FB" : "white",
              cursor: collaboratorProjectCount > 0 ? "pointer" : "not-allowed",
              opacity: collaboratorProjectCount > 0 ? 1 : 0.5,
            }}
            onClick={() => {
              if (collaboratorProjectCount > 0) {
                setAccessLevel("Collaborator");
                setPage(0);
                setProjectSearch("");
              }
            }}
            gap={"8px"}
            alignItems={"center"}
          >
            <Image src={COLLABORATORS} alt="ORG_ICON" height={16} width={16} />
            <VeTypography variant={"label3StrongMain"}>
              Collaborating Projects
            </VeTypography>
          </VeBox>
        </VeBox>
        <VeBox
          onKeyDown={(e) => {
            // Prevent Menu from handling keyboard events
            e.stopPropagation();
          }}
          onClick={(e) => {
            // Prevent Menu from closing when clicking on search
            e.stopPropagation();
          }}
        >
          <VeSearch
            name="search"
            style={{ width: "100%", marginBottom: "8px", padding: "0px 8px" }}
            placeHolder="Search for project"
            search
            clearSearch={projectSearch?.length > 0}
            clearSearchFunction={clearSearchFunction}
            value={projectSearch}
            onChange={handleProjectSearch}
          />
        </VeBox>
        {/* not showing the portfolio when searching */}
        {/* {projectSearch.trim() === "" && (
          <VeBox marginBottom={"8px"}>
            <VeTypography
              variant="label3StrongMain"
              sx={{ padding: "0px 16px" }}
            >
              Portfolio
            </VeTypography>
            <MenuItem
              onClick={() => {}}
              sx={{
                borderRadius: "4px",
                padding: "6px 16px",
                "&:hover": {
                  backgroundColor: "transparent",
                  cursor: "default",
                },
              }}
            >
              <VeBox
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                width={"100%"}
              >
                <VeTypography
                  variant="label2StrongDisabled"
                  sx={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {userData?.user?.organisationName || ""}
                </VeTypography>
              </VeBox>
            </MenuItem>
          </VeBox>
        )} */}

        {/* Show filtered results or empty state */}
        {filteredProjects.length > 0 ? (
          <>
            {/* <VeTypography
              variant="label3StrongerMain"
              sx={{ padding: "0px 16px" }}
            >
              Projects
            </VeTypography> */}
            {filteredProjects.map((project: any) => (
              <MenuItem
                key={project?.id}
                onClick={() => {
                  if (selectedProject?.id !== project?.id) {
                    handleProjectChange(project);
                  } else {
                    handleCloseUserMenuTab();
                  }
                }}
                sx={{
                  borderRadius: "4px",
                  padding: "8px 16px",
                  minHeight: "32px !important",
                  "&:hover": {
                    backgroundColor:
                      selectedProject?.id === project.id ? "transparent" : "",
                    cursor:
                      selectedProject?.id === project.id
                        ? "default"
                        : "pointer",
                  },
                }}
              >
                <VeBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width={"100%"}
                >
                  <VeBox width={"100%"} display="flex" alignItems="center">
                    <VeTypography
                      variant="label2StrongSecondary"
                      sx={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        width: "90%",
                      }}
                    >
                      {project.projectName}
                    </VeTypography>
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{
                        color: "#5F6368",
                        fontSize: "12px",
                        marginLeft: "7px",
                        marginRight: "8px",
                        width: "10%",
                        visibility:
                          selectedProject?.id === project.id
                            ? "visible"
                            : "hidden",
                      }}
                    />
                  </VeBox>
                  {!project.isSetupGuideCompleted && (
                    <VeBox
                      display="flex"
                      alignItems="center"
                      gap="4px"
                      padding="3px 10px"
                      borderRadius="50px"
                      sx={{ backgroundColor: "#F3F3F3", marginLeft: "auto" }}
                    >
                      <VeTypography variant="label4Secondary">
                        Setup
                      </VeTypography>
                    </VeBox>
                  )}
                </VeBox>
              </MenuItem>
            ))}
            {/* 👉 Add Project Button */}
            <MenuItem
              onClick={handleAddProject}
              sx={{
                borderRadius: "4px",
                padding: "6px 16px",
                minHeight: "32px !important",
              }}
            >
              <VeBox display="flex" alignItems="center" gap="8px">
                <VeTypography variant="label2StrongSecondary">
                  Add Project
                </VeTypography>
                <FontAwesomeIcon
                  icon={faPlus}
                  style={{ fontSize: "12px", color: "#5F6368" }}
                />
              </VeBox>
            </MenuItem>
            {data?.count > rowsPerPage && (
              <VeBox
                display="flex"
                alignItems="center"
                justifyContent={"center"}
                gap="8px"
                width={"100%"}
              >
                <VeTypography
                  onClick={showMoreProjects}
                  className="document_title"
                  variant="label3StrongBrand"
                  sx={{ cursor: "pointer" }}
                >
                  Show More
                </VeTypography>
              </VeBox>
            )}
          </>
        ) : isFetching && searchQuery?.length === 0 && page == 1 ? (
          <VeBox
            display="flex"
            justifyContent="center"
            alignItems={"center"}
            height={"100px"}
          >
            <CircularProgress size={15} sx={{ color: "#703DDE" }} />
            {/* CircularProgress loader */}
          </VeBox>
        ) : (
          <VeBox textAlign={"center"}>
            <VeTypography
              variant="label3Tertiary"
              sx={{
                padding: "12px 20px",
              }}
            >
              No projects available
            </VeTypography>
          </VeBox>
        )}
      </Menu>
      <OnboardingPageDrawer
        isOpen={addProject}
        onClose={() => setAddProject(false)}
        projectId={""}
        projectData={null}
        projectLoading={false}
        projectRefetch={() => { }}
        documentsRefetch={() => { }}
      />
    </VeBox>
  );
};

export default ProjectSelector;
